import type { Permission } from '@arquivo-morto/contracts';
import type { FluentIcon } from '@fluentui/react-icons';
import type { ComponentType } from 'react';

/** Uma página do módulo, listada na navegação lateral. */
export interface PageDef {
  /** Caminho relativo ao módulo (ex.: `pessoas`). */
  readonly path: string;
  readonly title: string;
  /** Uma linha sobre a página, mostrada no Início. */
  readonly description: string;
  readonly icon: FluentIcon;
  /** Permissão exigida. Sem ela, qualquer pessoa autenticada acessa. */
  readonly permission?: Permission;
  /**
   * Carrega a página sob demanda. Quem não tem a permissão nem baixa o
   * código. Devolve o componente exportado pelo nome (sem default export).
   */
  readonly load: () => Promise<ComponentType>;
}

/**
 * Rota do módulo que não aparece na navegação (detalhe, formulário). Fica
 * "dentro" de uma página: a navegação destaca a página e o breadcrumb mostra
 * as duas.
 */
export interface RouteDef {
  /** Caminho relativo ao módulo, com parâmetros (ex.: `caixas/:id`). */
  readonly path: string;
  readonly title: string;
  /** Permissão exigida. Sem ela, qualquer pessoa autenticada acessa. */
  readonly permission?: Permission;
  readonly load: () => Promise<ComponentType>;
}

/** Área funcional da aplicação: um grupo de páginas na navegação. */
export interface ModuleDef {
  /** Prefixo de rota, sem barra (ex.: `admin`). Também identifica o módulo. */
  readonly path: string;
  readonly title: string;
  readonly icon: FluentIcon;
  /** `main`: módulos de negócio. `footer`: utilitários, no fim da navegação. */
  readonly placement: 'main' | 'footer';
  readonly pages: readonly [PageDef, ...PageDef[]];
  readonly routes?: readonly RouteDef[];
}

export const pagePath = (module: ModuleDef, page: PageDef): string =>
  `/${module.path}/${page.path}`;
